import { createContext, useContext, useEffect, useState } from 'react';

type Props = {
  children: React.ReactNode;
};

type Step = {
  name: string;
  y: number;
  element: HTMLDivElement;
  state?: 'current' | 'completed';
};

type El = {
  name: string;
  element: HTMLDivElement;
};

type Context = {
  registerElement: (el: El) => void;
  steps: Step[];
  currentStep: Step;
  completedSteps: string[];
  setCompletedSteps: (step: string[]) => void;
  registerRef: (el: HTMLDivElement) => void;
  dynamicFormRef?: HTMLDivElement;
};

export const ScrollSpyContext = createContext<Context | null>(null);

export const ScrollSpyContextProvider = ({ children }: Props) => {
  const [steps, setSteps] = useState<Step[]>([]);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<Step>(steps[0]);
  const [dynamicFormRef, setDynamicFormRef] = useState<HTMLDivElement>();

  useEffect(() => {
    if (!dynamicFormRef) return;
    const listener = () => {
      let getCollapsedItems =
        dynamicFormRef.getElementsByClassName('collapsed-item');

      if (dynamicFormRef?.scrollTop > 10) {
        dynamicFormRef?.classList?.add('request-in-scroll');
      } else dynamicFormRef?.classList?.remove('request-in-scroll');

      let outerOffset =
        dynamicFormRef?.offsetTop +
        (getCollapsedItems && getCollapsedItems.length > 0
          ? getCollapsedItems.length * 110
          : 0);

      let currentStep =
        steps.find(({ y }) => dynamicFormRef.scrollTop < y - outerOffset) ||
        steps[steps.length - 1];

      if (
        dynamicFormRef.offsetHeight + dynamicFormRef.scrollTop ===
        dynamicFormRef.scrollHeight
      )
        currentStep = steps[steps.length - 1];

      const index = steps.indexOf(currentStep);
      setCurrentStep(currentStep);
      setCompletedSteps(steps.slice(0, index + 1).map((s) => s.name));
    };

    dynamicFormRef.addEventListener('scroll', listener);

    return () => {
      dynamicFormRef.removeEventListener('scroll', listener);
    };
  }, [steps, dynamicFormRef]);

  useEffect(() => {
    setCurrentStep(
      steps.find(({ y }) => window.scrollY * 1.5 < y) ||
        steps[steps.length - 1],
    );
  }, [steps, dynamicFormRef]);

  const registerElement = ({ name, element }: El) => {
    const y = element.offsetTop + element.offsetHeight;

    setSteps((steps) => {
      const stepsClone = [...steps];
      const stepIndex = steps.findIndex((st) => st.name === name);
      if (stepIndex === -1)
        return [...stepsClone, { name, y, element }].sort((a, b) => a.y - b.y);
      else {
        stepsClone.map(
          (st) => (st.y = st.element.offsetTop + st.element.offsetHeight),
        );
      }
      return stepsClone;
    });
  };

  const registerRef = (el: HTMLDivElement) => {
    setDynamicFormRef(el);
  };
  return (
    <ScrollSpyContext.Provider
      value={{
        registerElement,
        currentStep,
        steps,
        completedSteps,
        setCompletedSteps,
        registerRef,
        dynamicFormRef,
      }}
    >
      {children}
    </ScrollSpyContext.Provider>
  );
};

export const useScrollSpyContext = () => {
  const context = useContext(ScrollSpyContext);

  if (!context)
    throw new Error(
      'ScrollSpyContext must be called from within the ScrollSpyContextProvider',
    );

  return context;
};
